/**
 * One short, plain line under each page title — easy for transporters to understand.
 * When `options.total` is set, subtitle shows only the system-wide total.
 */
export const PAGE_SUBTITLES: Record<string, string> = {
  dashboard: 'See today\'s trips, vehicles and money in one place',

  trips: 'Add a trip, give vehicle and driver, then track status',
  bilty: 'Make bilty (LR) — sender, receiver, goods and freight',
  bilty_form: 'Fill vehicle, parties, goods and charges',
  bilty_items: 'Save common goods names to pick fast on bilty',
  consignors: 'People or companies who send the goods',
  consignees: 'People or companies who receive the goods',
  invoices: 'Bills made from bilty — paid or pending',
  invoice_unlinked: 'Link a bilty to print LR and see freight',

  clients: 'Your clients — name, phone, address and credit',

  vehicles: 'All trucks and vehicles — number plate, type, make, model and documents',
  vehicle_types: 'Types of vehicles (from master list)',
  vehicle_makes: 'Vehicle brands (from master list)',
  vehicle_models: 'Vehicle models linked to brand and type',
  vehicle_documents: 'RC, insurance, fitness, PUC and other documents with expiry',
  drivers: 'Drivers — licence, phone and documents',
  helpers: 'Helpers who travel with the vehicle',
  gps_tracking: 'See where vehicles are right now on the map',
  gps_devices: 'GPS boxes fitted on vehicles',
  gps_alerts: 'Warnings — overspeed, offline, geofence',
  gps_reports: 'Past travel, distance and speed reports',
  fuel: 'Diesel / petrol filled — vehicle, litres and amount',
  maintenance: 'Service and repair — workshop and cost',
  puncture: 'Puncture repair — place, shop and cost',
  battery_maintenance: 'Battery change or charge — brand and amount',
  cash_memo: 'Daily cash in and cash out',
  fastag: 'FASTag balance, trips and licence plate',
  fleet_reports: 'Month summary — cash, FASTag and service by vehicle',

  branches: 'Your office / branch locations',
  roles: 'Who can login and what they can do',
  staff: 'Staff details linked to branches',

  documents: 'All documents — expired, expiring soon, or valid',
  reports: 'Ready reports — pick dates and download',
  guide: 'Simple help to use this software',

  settings: 'Company logo, colours and print options',
  integrations: 'Connect WhatsApp, payments and other tools',
  module_settings: 'Change menu names for your company',
  field_management: 'Show or hide form fields',
  layout_builder: 'Arrange form fields the way you like',
  activity: 'See who added, changed or deleted something',

  super_admin_dashboard: 'Platform overview',
  tenants: 'All transport companies on the platform',
  pending_approvals: 'New company sign-ups waiting for approval',
  subscriptions: 'Tenant plans and billing status',
  plans: 'Subscription plans and pricing',
  billing_invoices: 'Platform invoices for tenants',
  transactions: 'Payment transactions',
  platform_settings: 'Global platform settings',
  platform_billing_profile: 'Your company details on platform invoices',
  platform_integrations: 'Platform-level integrations',
  core_modules: 'Turn core modules on or off',
  tenant_module_access: 'Which modules each tenant can use',
  tenant_report_access: 'Which reports each tenant can run',
  report_templates: 'Report templates for tenants',
  feature_flags: 'Feature flags',
  field_types: 'Field types for forms',
  relationship_rules: 'How modules link to each other',
  security: 'Login and security logs',
  analytics: 'Platform analytics',
  crm_modules: 'CRM modules',
  super_admin_users: 'Super admin user accounts',
  master_vehicle_type: 'Shared vehicle types for all companies',
  master_vehicle_make: 'Shared brands for all companies',
  master_vehicle_model: 'Shared models for all companies',
};

const TOTAL_UNITS: Record<string, [string, string]> = {
  vehicles: ['vehicle', 'vehicles'],
  clients: ['client', 'clients'],
  drivers: ['driver', 'drivers'],
  helpers: ['helper', 'helpers'],
  trips: ['trip', 'trips'],
  bilty: ['bilty', 'bilties'],
  invoices: ['bill', 'bills'],
  fuel: ['fuel entry', 'fuel entries'],
  maintenance: ['service record', 'service records'],
  puncture: ['puncture record', 'puncture records'],
  cash_memo: ['cash entry', 'cash entries'],
  fastag: ['FASTag entry', 'FASTag entries'],
  battery_maintenance: ['battery record', 'battery records'],
  branches: ['branch', 'branches'],
  consignors: ['sender', 'senders'],
  consignees: ['receiver', 'receivers'],
  staff: ['staff member', 'staff members'],
  roles: ['role', 'roles'],
  documents: ['document', 'documents'],
  bilty_items: ['goods name', 'goods names'],
  vehicle_documents: ['vehicle', 'vehicles'],
  vehicle_types: ['vehicle type', 'vehicle types'],
  vehicle_makes: ['brand', 'brands'],
  vehicle_models: ['model', 'models'],
  gps_devices: ['GPS box', 'GPS boxes'],
  gps_alerts: ['GPS alert', 'GPS alerts'],
  gps_tracking: ['vehicle', 'vehicles'],
  fleet_reports: ['vehicle', 'vehicles'],
  reports: ['report', 'reports'],
  activity: ['change', 'changes'],
};

/** Resolve subtitle for a page or module key; optional label for unknown custom CRM keys */
export function getPageSubtitle(
  moduleKey: string,
  label?: string,
  options?: { total?: number },
): string | undefined {
  const key = moduleKey.replace(/\//g, '_');

  if (options?.total != null && Number.isFinite(options.total)) {
    const n = Math.max(0, Math.floor(options.total));
    const pair = TOTAL_UNITS[key];
    if (pair) return `Total ${n} ${n === 1 ? pair[0] : pair[1]}`;
    const base = (label || key.replace(/_/g, ' ') || 'record').trim().toLowerCase();
    if (n === 1) {
      const singular = base.endsWith('s') ? base.slice(0, -1) : base;
      return `Total ${n} ${singular}`;
    }
    return `Total ${n} ${base}`;
  }

  let text: string | undefined = PAGE_SUBTITLES[key];

  if (!text) {
    const masterMatch = key.match(/^master_(vehicle_type|vehicle_make|vehicle_model)$/);
    if (masterMatch) text = PAGE_SUBTITLES[`master_${masterMatch[1]}`];
  }

  if (!text && label) {
    text = `Add and manage ${label.toLowerCase()}`;
  }

  return text;
}
